/**
 * SummaryOrderReview — wires the store to `OrderReviewSection`.
 *
 * Split from the section so the section stays a pure render (its notice rules
 * are the part worth testing without a store, a catalogue query or a feature
 * flag mock), and so `Summary.page` can hand one element to the legacy
 * `reviewSlot`.
 */

import React, { useCallback } from 'react'

import { formatPrice } from 'utils/price/priceFormatter'

import { usePfStore } from '../../store'
import type { CartDisplayRow, CartExtraRow } from '../../components/cart/useCartDisplay'
import { OrderReviewSection } from './OrderReviewSection.component'
import { useOrderReviewLines } from './useOrderReviewLines'

export const SummaryOrderReview: React.FC = () => {
  const { lines, extras, currency } = useOrderReviewLines()
  const comments = usePfStore((s) => s.comments)
  const regionCode = usePfStore((s) => s.property?.regionCode)

  /** The cart sidebar's own formatting, so one cart cannot show two prices. */
  const formatLinePrice = useCallback((row: CartDisplayRow) => (
    row.lineTotal && row.currency ? formatPrice(row.lineTotal, row.currency) : ''
  ), [])

  /** The saving is recapped here too, so the review shows the pair the cart showed.
   *  Null for every line without one, which is the ordinary case. */
  const formatBasePrice = useCallback((row: CartDisplayRow) => (
    row.baseLineTotal && row.currency ? formatPrice(row.baseLineTotal, row.currency) : null
  ), [])

  /** Extras carry no currency of their own — they inherit the cart's, exactly
   *  as the rail's extra lines do. */
  const formatExtraPrice = useCallback((extra: CartExtraRow) => (
    formatPrice(extra.amount, currency)
  ), [currency])

  const formatExtraBasePrice = useCallback((extra: CartExtraRow) => (
    extra.baseAmount ? formatPrice(extra.baseAmount, currency) : null
  ), [currency])

  return (
    <OrderReviewSection
      lines={lines}
      extras={extras}
      regionCode={regionCode}
      comments={comments}
      formatLinePrice={formatLinePrice}
      formatBasePrice={formatBasePrice}
      formatExtraPrice={formatExtraPrice}
      formatExtraBasePrice={formatExtraBasePrice}
    />
  )
}
